<script lang="ts">
import type { ValueOf } from '#layers/base/public';
import type { BadgeSlots, BadgeProps as UBadgeProps } from '#ui/types';
import { tv } from 'tailwind-variants';
import { getColorInfo } from '#layers/ui/utils/colors';

/**
 * Border radius treatment of a badge.
 *
 * `Normal` keeps the theme radius for the current size; `Full` is the pill
 * (or circle, when the badge is square) that marks it as an indicator.
 *
 */
export const RoundedVariant = {
	Normal: 'normal',
	Full: 'full',
} as const;

export type RoundedVariant = ValueOf<typeof RoundedVariant>;

export interface BadgeProps extends Omit<UBadgeProps, 'color' | 'variant'> {
	color?: UBadgeProps['color'] | 'custom';
	variant?: UBadgeProps['variant'] | 'outline-neutral';
	customColor?: string;
	rounded?: RoundedVariant;
}

const badge = tv({
	variants: {
		rounded: {
			normal: '',
			full: 'rounded-full',
		},
		leading: {
			true: '',
		},
		trailing: {
			true: '',
		},
		size: {
			xs: '',
			sm: '',
			md: '',
			lg: '',
			xl: '',
		},
	},
	compoundVariants: [
		{ size: 'xs', leading: true, class: 'pe-2' },
		{ size: ['sm', 'md', 'lg'], leading: true, class: 'pe-2.5' },
		{ size: 'xl', leading: true, class: 'pe-3' },
		{ size: 'xs', trailing: true, class: 'ps-2' },
		{ size: ['sm', 'md', 'lg'], trailing: true, class: 'ps-2.5' },
		{ size: 'xl', trailing: true, class: 'ps-3' },
	],
	defaultVariants: {
		size: 'md',
	},
});
</script>

<script setup lang="ts">
import { reactiveOmit } from '@vueuse/core';
import { computed } from 'vue';

import Badge from '#ui/components/Badge.vue';
import { useFieldGroup } from '#ui/composables/useFieldGroup';

const props = withDefaults(defineProps<BadgeProps>(), {
	rounded: RoundedVariant.Full,
});
const slots = defineSlots<BadgeSlots>();

const forwarded = reactiveOmit(props, ['customColor', 'rounded']);

const { size } = useFieldGroup<BadgeProps>(props);

const hasLeadingIcon = computed(() => !!props.leadingIcon || (!!props.icon && (props.leading || !props.trailing)));
const hasTrailingIcon = computed(() => !!props.trailingIcon || (!!props.icon && !!props.trailing));
const isSquare = computed(() => props.square || (!slots.default && !props.label));

const badgeProps = computed(() => {
	return {
		...forwarded,
		color: props.color as UBadgeProps['color'],
		variant: props.variant as UBadgeProps['variant'],
		class: badge({
			rounded: props.rounded,
			size: size.value,
			leading: !isSquare.value && hasLeadingIcon.value,
			trailing: !isSquare.value && hasTrailingIcon.value,
			class: props.class,
		}),
	};
});

const badgeStyle = computed(() => {
	const { color, customColor, variant } = props;

	if (color === 'custom' && customColor) {
		const { isLight, isDark } = getColorInfo(customColor);

		const baseStyle = {
			'--badge-background': customColor,
		};

		if (variant === 'soft' || variant === 'subtle') {
			return {
				...baseStyle,
				'--badge-text': 'var(--ui-text-highlighted)',
				'--badge-background': customColor,
			};
		}
		else if (variant === 'outline') {
			return {
				...baseStyle,
				'--badge-text': 'var(--ui-text-highlighted)',
				'--badge-ring': isLight ? 'var(--ui-border-highlighted)' : (isDark ? `light-dark(${customColor}, var(--ui-text))` : customColor),
			};
		}
		else {
			return {
				...baseStyle,
				'--badge-text': isLight
					? 'light-dark(var(--ui-text), var(--ui-text-inverted))'
					: 'light-dark(var(--ui-text-inverted), var(--ui-text))',
			};
		}
	}

	return {};
});
</script>

<template>
	<Badge
		v-bind="badgeProps"
		:style="badgeStyle"
	>
		<template v-for="(_, name) in slots" #[name]="slotData">
			<slot :name v-bind="slotData" />
		</template>
	</Badge>
</template>
